import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import { useSubscription } from "@/lib/subscription";
import { Plus, Package, AlertTriangle, Clock, IndianRupee, Search, Edit3, Trash2, ArrowDownCircle, ArrowUpCircle, X, Layers, History, Truck, MinusCircle, Sliders, Boxes, FlaskConical } from "lucide-react";
import InventoryTabs from "@/components/inventory/InventoryTabs";
import { DateDMYInput } from "@/components/DateDMYInput";

const CATEGORIES = [
  "Consumables", "Instruments", "Medications", "Restorative Materials",
  "Endodontic", "Prosthetic", "Orthodontic", "Sterilisation", "Office Supplies", "Other",
];
const UNITS = ["piece", "box", "tube", "pack", "ml", "mg", "gram", "bottle", "vial", "cartridge"];

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function Inventory() {
  const { sub, hasFeature } = useSubscription();
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [filter, setFilter] = useState("all");   // all | low | expiring
  const [editing, setEditing] = useState(null);  // item being edited (or {} for new)
  const [movement, setMovement] = useState(null); // { item, kind }
  const [history, setHistory] = useState(null);   // item with movements[]
  // Inventory V2 — three new actions. Each opens a drawer that hits the FIFO
  // engine directly (bypassing the legacy `movement` modal which is retained
  // for row-level "In / Out" quick actions during migration).
  const [dash, setDash]           = useState(null);
  const [grnOpen, setGrnOpen]     = useState(null);       // {item?} or null
  const [consumeOpen, setConsumeOpen] = useState(null);
  const [adjustOpen, setAdjustOpen]   = useState(null);

  const canWrite = ["admin", "doctor"].includes(sub?.plan ? "admin" : "admin"); // gated at API layer

  const loadAll = async () => {
    try {
      const params = new URLSearchParams();
      if (category !== "all") params.append("category", category);
      if (filter === "low") params.append("low_stock", "true");
      if (filter === "expiring") params.append("expiring_days", "30");
      if (q.trim()) params.append("search", q.trim());
      const [it, sm, dh] = await Promise.all([
        api.get(`/inventory/items?${params.toString()}`),
        api.get("/inventory/summary"),
        // Dashboard aggregation — safe to fetch alongside; the FIFO engine
        // guarantees `stock` is always the sum of remaining lots, so cards
        // match the table on every render.
        api.get("/inventory/dashboard").catch(() => ({ data: null })),
      ]);
      setItems(it.data);
      setSummary(sm.data);
      setDash(dh.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.error === "upgrade_required") setSummary({ locked: true });
      else toast.error(typeof d === "string" ? d : "Failed to load inventory");
    }
  };

  useEffect(() => { loadAll(); }, [category, filter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(loadAll, 300); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!hasFeature("inventory") && summary?.locked !== false) {
    return (
      <div className="max-w-lg mx-auto py-16 text-center">
        <Package className="w-10 h-10 mx-auto text-ink-soft" />
        <h1 className="mt-4 font-display text-3xl">Inventory is a Clinic plan feature</h1>
        <p className="mt-2 text-sm text-ink-mid">Track materials, stock levels, expiry dates, and value. Upgrade to unlock.</p>
        <a href="/app/subscription" className="btn-primary mt-6 inline-block">See plans</a>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <InventoryTabs />
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="overline text-ink-soft">Inventory</span>
          <h1 className="mt-2 font-display text-4xl tracking-tight">Materials &amp; stock.</h1>
          <p className="mt-2 text-sm text-ink-mid max-w-xl">
            Receive stock, deduct consumption, and audit every movement — all
            through the same FIFO engine your treatment kits already use.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="btn-grn" onClick={() => setGrnOpen({})}
                  className="inline-flex items-center gap-1.5 rounded-full bg-pine text-linen px-4 py-2 text-sm hover:bg-pine/90">
            <Truck className="w-4 h-4" /> Receive stock
          </button>
          <button data-testid="btn-consume" onClick={() => setConsumeOpen({})}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white text-ink px-4 py-2 text-sm border border-[#E8EAE9] hover:border-pine">
            <MinusCircle className="w-4 h-4" /> Manual consumption
          </button>
          <button data-testid="btn-adjust" onClick={() => setAdjustOpen({})}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white text-ink px-4 py-2 text-sm border border-[#E8EAE9] hover:border-pine">
            <Sliders className="w-4 h-4" /> Adjustment
          </button>
          <button data-testid="btn-new-item" onClick={() => setEditing({})}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white text-ink px-4 py-2 text-sm border border-[#E8EAE9] hover:border-pine">
            <Plus className="w-4 h-4" /> New product
          </button>
        </div>
      </div>

      {/* V2 Dashboard cards — 6-card top row.  Every count comes from the
          `/inventory/dashboard` aggregation which reads through the FIFO
          engine, so these numbers can never disagree with the product table. */}
      {dash?.cards && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="inv-dashboard-cards">
          <Stat icon={IndianRupee}   label="Inventory value"    value={INR(dash.cards.inventory_value)} highlight testid="card-value" />
          <Stat icon={Boxes}         label="Total products"     value={dash.cards.total_products}                   testid="card-total-products" />
          <Stat icon={AlertTriangle} label="Low stock"          value={dash.cards.low_stock}     warn={dash.cards.low_stock > 0}     testid="card-low-stock" />
          <Stat icon={Package}       label="Out of stock"       value={dash.cards.out_of_stock}  warn={dash.cards.out_of_stock > 0}  testid="card-out-of-stock" />
          <Stat icon={Clock}         label="Expiring · 60d"     value={dash.cards.expiring_soon} warn={dash.cards.expiring_soon > 0} testid="card-expiring" />
          <Stat icon={FlaskConical}  label="Today's consumption" value={dash.cards.todays_consumption}              testid="card-today-consumption" />
        </div>
      )}
      {/* Legacy 4-card row kept as a fallback if the dashboard endpoint fails. */}
      {!dash?.cards && summary && !summary.locked && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat icon={Package} label="Items" value={summary.total_items} testid="stat-items" />
          <Stat icon={IndianRupee} label="Inventory value" value={INR(summary.total_value_inr)} highlight testid="stat-value" />
          <Stat icon={AlertTriangle} label="Low stock" value={summary.low_stock_count} warn={summary.low_stock_count > 0} testid="stat-low" />
          <Stat icon={Clock} label="Expiring · 30d" value={summary.expiring_count} warn={summary.expiring_count > 0} testid="stat-expiring" />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[240px] relative">
          <Search className="w-4 h-4 text-ink-soft absolute left-3 top-3" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items…"
                 data-testid="inv-search"
                 className="w-full pl-9 pr-3 py-2 rounded-full border border-[#E8EAE9] bg-white text-sm focus:outline-none focus:border-pine" />
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)}
                data-testid="inv-category-filter"
                className="px-3 py-2 rounded-full border border-[#E8EAE9] bg-white text-sm">
          <option value="all">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="inline-flex rounded-full border border-[#E8EAE9] p-1 bg-white text-xs" data-testid="inv-filter">
          {[
            { id: "all", label: "All" },
            { id: "low", label: `Low stock${summary?.low_stock_count ? ` · ${summary.low_stock_count}` : ""}` },
            { id: "expiring", label: `Expiring${summary?.expiring_count ? ` · ${summary.expiring_count}` : ""}` },
          ].map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`inv-filter-${f.id}`}
                    className={`px-3 py-1.5 rounded-full ${filter === f.id ? "bg-pine text-linen" : "text-ink-mid"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="card-widget p-0 overflow-hidden">
        {items.length === 0 ? (
          <div className="py-16 text-center text-ink-soft text-sm">
            <Layers className="w-8 h-8 mx-auto text-ink-disabled mb-3" />
            No items yet — click <strong>New item</strong> to add your first material.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-soft overline bg-linen/60">
                <th className="px-4 py-3">Item</th>
                <th>Category</th>
                <th>Stock</th>
                <th>Reorder</th>
                <th>Expiry</th>
                <th className="text-right">Unit cost</th>
                <th className="text-right pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => <ItemRow key={i.id} item={i}
                                          onEdit={() => setEditing(i)}
                                          onMove={(kind) => setMovement({ item: i, kind })}
                                          onHistory={() => setHistory(i)}
                                          onDelete={async () => {
                                            if (!window.confirm(`Delete ${i.name}?`)) return;
                                            try { await api.delete(`/inventory/items/${i.id}`); toast.success("Deleted"); loadAll(); }
                                            catch (e) { toast.error(apiError(e, "Delete failed")); }
                                          }} />)}
            </tbody>
          </table>
        )}
      </div>

      {/* Modals */}
      {editing !== null && (
        <ItemEditor item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); loadAll(); }} />
      )}
      {movement && (
        <MovementDialog item={movement.item} kind={movement.kind}
                        onClose={() => setMovement(null)} onSaved={() => { setMovement(null); loadAll(); }} />
      )}
      {history && (
        <HistoryDialog item={history} onClose={() => setHistory(null)} />
      )}
      {/* V2 drawers — each hits the FIFO engine directly. */}
      {grnOpen && (
        <GRNDialog items={items} onClose={() => setGrnOpen(null)}
                   onSaved={() => { setGrnOpen(null); loadAll(); }} />
      )}
      {consumeOpen && (
        <ConsumeDialog items={items} onClose={() => setConsumeOpen(null)}
                       onSaved={() => { setConsumeOpen(null); loadAll(); }} />
      )}
      {adjustOpen && (
        <AdjustDialog items={items} onClose={() => setAdjustOpen(null)}
                      onSaved={() => { setAdjustOpen(null); loadAll(); }} />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function Stat({ icon: Icon, label, value, highlight, warn, testid }) {
  return (
    <div className={`rounded-2xl p-5 border ${highlight ? "bg-pine text-linen border-pine" : warn ? "bg-[#FCECEC] border-[#EDC7C6]" : "bg-white border-[#E8EAE9]"}`} data-testid={testid}>
      <Icon className={`w-4 h-4 ${highlight ? "text-linen/70" : warn ? "text-[#B94A48]" : "text-ink-mid"}`} />
      <div className={`mt-2 font-display text-3xl ${warn ? "text-[#B94A48]" : ""}`}>{value}</div>
      <div className={`mt-1 text-xs uppercase tracking-widest ${highlight ? "text-linen/70" : "text-ink-soft"}`}>{label}</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function ItemRow({ item, onEdit, onMove, onHistory, onDelete }) {
  const stock = Number(item.stock || 0);
  const reorder = Number(item.reorder_level || 0);
  const isLow = reorder > 0 && stock <= reorder;
  const pct = reorder > 0 ? Math.min(100, (stock / (reorder * 2)) * 100) : 100;
  const now = new Date();
  const exp = item.expiry_date ? new Date(item.expiry_date) : null;
  const daysToExp = exp ? Math.round((exp - now) / 86400000) : null;
  const expClass = daysToExp === null ? "text-ink-soft"
    : daysToExp < 0 ? "text-[#B94A48] font-medium"
    : daysToExp <= 30 ? "text-[#D06A42] font-medium" : "text-ink-mid";

  return (
    <tr className="border-t border-[#E8EAE9]" data-testid={`inv-row-${item.id}`}>
      <td className="px-4 py-3">
        <div className="font-medium">{item.name}</div>
        {item.sku && <div className="text-[10px] font-mono text-ink-soft uppercase tracking-widest">{item.sku}</div>}
      </td>
      <td className="text-ink-mid text-xs">{item.category}</td>
      <td>
        <div className="font-display text-lg">{stock} <span className="text-xs text-ink-soft">{item.unit}</span></div>
        <div className="mt-1 h-1 w-24 bg-linen rounded-full overflow-hidden">
          <div className="h-full transition-all" style={{ width: `${pct}%`, background: isLow ? "#B94A48" : "#1A362B" }} />
        </div>
      </td>
      <td className="text-ink-mid text-xs">
        {reorder > 0 ? `≤ ${reorder}` : "—"}
        {isLow && <div className="mt-0.5 text-[10px] uppercase tracking-widest text-[#B94A48]">Reorder</div>}
      </td>
      <td className={`text-xs ${expClass}`}>
        {exp ? exp.toLocaleDateString("en-IN") : "—"}
        {daysToExp !== null && daysToExp >= 0 && daysToExp <= 30 && <div className="text-[10px]">{daysToExp}d left</div>}
        {daysToExp !== null && daysToExp < 0 && <div className="text-[10px]">Expired</div>}
      </td>
      <td className="text-right font-display">{INR(item.unit_cost)}</td>
      <td className="text-right pr-4">
        <div className="inline-flex gap-1">
          <IconBtn tip="Stock in"  testid={`inv-in-${item.id}`}  onClick={() => onMove("in")}><ArrowDownCircle className="w-4 h-4 text-[#2D6A4F]" /></IconBtn>
          <IconBtn tip="Stock out" testid={`inv-out-${item.id}`} onClick={() => onMove("out")}><ArrowUpCircle className="w-4 h-4 text-[#D06A42]" /></IconBtn>
          <IconBtn tip="History"   testid={`inv-hist-${item.id}`} onClick={onHistory}><History className="w-4 h-4 text-ink-mid" /></IconBtn>
          <IconBtn tip="Edit"      testid={`inv-edit-${item.id}`} onClick={onEdit}><Edit3 className="w-4 h-4 text-ink-mid" /></IconBtn>
          <IconBtn tip="Delete"    testid={`inv-del-${item.id}`} onClick={onDelete}><Trash2 className="w-4 h-4 text-ink-mid hover:text-[#B94A48]" /></IconBtn>
        </div>
      </td>
    </tr>
  );
}

function IconBtn({ children, onClick, tip, testid }) {
  return (
    <button type="button" onClick={onClick} title={tip} data-testid={testid}
            className="w-8 h-8 rounded-lg hover:bg-linen grid place-items-center">
      {children}
    </button>
  );
}

// -----------------------------------------------------------------------------
function ItemEditor({ item, onClose, onSaved }) {
  const isEdit = !!item.id;
  const [form, setForm] = useState({
    name: item.name || "",
    sku: item.sku || "",
    category: item.category || "Consumables",
    unit: item.unit || "piece",
    stock: item.stock ?? 0,
    reorder_level: item.reorder_level ?? 0,
    reorder_qty: item.reorder_qty ?? 0,
    unit_cost: item.unit_cost ?? 0,
    gst_rate: item.gst_rate ?? 18,
    expiry_date: item.expiry_date ? item.expiry_date.slice(0, 10) : "",
    storage_location: item.storage_location || "",
    supplier_name: item.supplier_name || "",
    notes: item.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        stock: Number(form.stock), reorder_level: Number(form.reorder_level),
        reorder_qty: Number(form.reorder_qty), unit_cost: Number(form.unit_cost),
        gst_rate: Number(form.gst_rate),
        expiry_date: form.expiry_date || null,
      };
      if (isEdit) await api.patch(`/inventory/items/${item.id}`, payload);
      else       await api.post("/inventory/items", payload);
      toast.success(isEdit ? "Item updated" : "Item added");
      onSaved();
    } catch (err) { toast.error(apiError(err, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={isEdit ? "Edit item" : "New item"} onClose={onClose}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Name *"><input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="inv-form-name" className="input" /></Field>
        <Field label="SKU / Code"><input value={form.sku} onChange={(e) => set("sku", e.target.value)} data-testid="inv-form-sku" className="input font-mono" /></Field>
        <Field label="Category">
          <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="inv-form-category" className="input">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Unit">
          <select value={form.unit} onChange={(e) => set("unit", e.target.value)} className="input">
            {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </Field>
        <Field label="Opening stock"><input type="number" step="0.01" value={form.stock} onChange={(e) => set("stock", e.target.value)} data-testid="inv-form-stock" className="input" /></Field>
        <Field label="Reorder level"><input type="number" step="0.01" value={form.reorder_level} onChange={(e) => set("reorder_level", e.target.value)} data-testid="inv-form-reorder" className="input" /></Field>
        <Field label="Reorder quantity"><input type="number" step="0.01" value={form.reorder_qty} onChange={(e) => set("reorder_qty", e.target.value)} className="input" /></Field>
        <Field label="Unit cost (₹)"><input type="number" step="0.01" value={form.unit_cost} onChange={(e) => set("unit_cost", e.target.value)} data-testid="inv-form-cost" className="input" /></Field>
        <Field label="GST rate (%)"><input type="number" step="0.01" value={form.gst_rate} onChange={(e) => set("gst_rate", e.target.value)} className="input" /></Field>
        <Field label="Expiry date"><DateDMYInput value={form.expiry_date} onChange={(e) => set("expiry_date", e.target.value)} className="input" /></Field>
        <Field label="Storage location"><input value={form.storage_location} onChange={(e) => set("storage_location", e.target.value)} placeholder="Cabinet A2" className="input" /></Field>
        <Field label="Supplier"><input value={form.supplier_name} onChange={(e) => set("supplier_name", e.target.value)} placeholder="3M India" className="input" /></Field>
        <div className="md:col-span-2">
          <Field label="Notes"><textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="input resize-none" /></Field>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button onClick={onClose} className="text-sm text-ink-mid hover:text-ink px-4 py-2">Cancel</button>
        <button onClick={save} disabled={saving} data-testid="inv-form-save" className="btn-primary text-sm">
          {saving ? "Saving…" : isEdit ? "Save changes" : "Add item"}
        </button>
      </div>
      <StyleShim />
    </Modal>
  );
}

// -----------------------------------------------------------------------------
function MovementDialog({ item, kind, onClose, onSaved }) {
  const [qty, setQty] = useState(1);
  const [cost, setCost] = useState(item.unit_cost || 0);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!qty || Number(qty) <= 0) { toast.error("Quantity must be > 0"); return; }
    setSaving(true);
    try {
      const payload = { kind, qty: Number(qty), notes };
      if (kind === "in") payload.unit_cost = Number(cost);
      await api.post(`/inventory/items/${item.id}/movement`, payload);
      toast.success(kind === "in" ? "Stock added" : "Stock removed");
      onSaved();
    } catch (err) { toast.error(apiError(err, "Failed")); }
    finally { setSaving(false); }
  };

  const title = kind === "in" ? `Stock in — ${item.name}` : `Stock out — ${item.name}`;
  return (
    <Modal title={title} onClose={onClose}>
      <div className="text-xs text-ink-mid mb-4">Current stock: <strong>{item.stock} {item.unit}</strong></div>
      <div className="space-y-4">
        <Field label={`Quantity (${item.unit})`}>
          <input type="number" step="0.01" min="0" value={qty} onChange={(e) => setQty(e.target.value)}
                 data-testid="inv-mv-qty" className="input" autoFocus />
        </Field>
        {kind === "in" && (
          <Field label="Unit cost (₹)">
            <input type="number" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} data-testid="inv-mv-cost" className="input" />
          </Field>
        )}
        <Field label="Notes">
          <input value={notes} onChange={(e) => setNotes(e.target.value)}
                 placeholder={kind === "in" ? "Purchased from supplier" : "Used in treatment / expired"}
                 className="input" />
        </Field>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button onClick={onClose} className="text-sm text-ink-mid hover:text-ink px-4 py-2">Cancel</button>
        <button onClick={save} disabled={saving} data-testid="inv-mv-save" className={`text-sm px-4 py-2 rounded-full text-linen ${kind === "in" ? "bg-[#2D6A4F]" : "bg-[#D06A42]"} hover:opacity-90`}>
          {saving ? "Saving…" : (kind === "in" ? "Add stock" : "Remove stock")}
        </button>
      </div>
      <StyleShim />
    </Modal>
  );
}

// -----------------------------------------------------------------------------
/** ProductDetailDialog — 5-tab drawer per the audit spec.
 *  Data sources:
 *    - /inventory/items/{id}/movements      → full movement history (v1 + v2)
 *    - /inventory/products/{id}/lots        → lots + avg/last cost + on_hand
 *  Movement rows now carry `balance_before` + `balance_after` + patient/
 *  treatment/batch refs, so every row can independently answer the audit
 *  questions (who / what / when / why / patient / treatment / batch /
 *  before / after). */
function HistoryDialog({ item, onClose }) {
  const [tab, setTab] = useState("overview");
  const [mv, setMv] = useState([]);
  const [detail, setDetail] = useState(null);   // { lots, on_hand, average_cost, last_cost, item }

  useEffect(() => {
    api.get(`/inventory/items/${item.id}/movements`).then((r) => setMv(r.data)).catch(() => {});
    api.get(`/inventory/products/${item.id}/lots`).then((r) => setDetail(r.data)).catch(() => {});
  }, [item.id]);

  const purchases   = mv.filter((m) => (m.movement_type || m.kind) === "receive" || m.kind === "in");
  const consumption = mv.filter((m) => ["manual_consume", "treatment"].includes(m.movement_type) || m.kind === "out");
  const inventoryValue = (detail?.lots || []).reduce(
    (s, l) => s + (Number(l.qty_remaining) || 0) * (Number(l.unit_cost) || 0), 0,
  );

  const TABS = [
    { id: "overview",    label: "Overview" },
    { id: "movements",   label: `Movements · ${mv.length}` },
    { id: "batches",     label: `Batches · ${detail?.lots?.length || 0}` },
    { id: "purchases",   label: `Purchases · ${purchases.length}` },
    { id: "consumption", label: `Consumption · ${consumption.length}` },
  ];

  return (
    <Modal title={item.name} onClose={onClose}>
      {/* Tab strip */}
      <div className="flex flex-wrap gap-1 border-b border-[#E8EAE9] -mt-2 mb-4 pb-0" data-testid="product-detail-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
                  data-testid={`pd-tab-${t.id}`}
                  className={`px-3 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${
                    tab === t.id ? "border-pine text-pine" : "border-transparent text-ink-soft hover:text-ink-mid"
                  }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-2 gap-3" data-testid="pd-overview">
          <OverviewCard label="Current stock"    value={`${Number(detail?.on_hand ?? item.stock ?? 0)} ${item.unit || ""}`} />
          <OverviewCard label="Average cost"     value={`₹${Number(detail?.average_cost ?? item.unit_cost ?? 0).toLocaleString("en-IN")}`} />
          <OverviewCard label="Inventory value"  value={`₹${Math.round(inventoryValue).toLocaleString("en-IN")}`} highlight />
          <OverviewCard label="Last cost"        value={`₹${Number(detail?.last_cost ?? 0).toLocaleString("en-IN")}`} />
          <OverviewCard label="Category"         value={item.category || "—"} />
          <OverviewCard label="Reorder level"    value={`≤ ${item.reorder_level || 0} ${item.unit || ""}`} />
        </div>
      )}

      {tab === "movements"   && <MovementList rows={mv}          unit={item.unit} testid="pd-movements" />}
      {tab === "batches"     && <BatchList    lots={detail?.lots || []}  unit={item.unit} />}
      {tab === "purchases"   && <MovementList rows={purchases}   unit={item.unit} emptyText="No purchases recorded yet." testid="pd-purchases" />}
      {tab === "consumption" && <MovementList rows={consumption} unit={item.unit} emptyText="No consumption recorded yet." testid="pd-consumption" />}
    </Modal>
  );
}

function OverviewCard({ label, value, highlight }) {
  return (
    <div className={`rounded-2xl border px-4 py-3 ${highlight ? "border-pine bg-pine/5" : "border-[#E8EAE9] bg-white"}`}>
      <div className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</div>
      <div className={`mt-1 font-display text-2xl ${highlight ? "text-pine" : ""}`}>{value}</div>
    </div>
  );
}

function BatchList({ lots, unit }) {
  if (!lots.length) return <EmptyState text="No batches yet — receive stock to seed the first lot." />;
  return (
    <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="pd-batches">
      {lots.map((l) => {
        const remaining = Number(l.qty_remaining || 0);
        const received  = Number(l.qty_received  || 0);
        const consumedPct = received > 0 ? Math.round(((received - remaining) / received) * 100) : 0;
        return (
          <div key={l.id} className="rounded-xl border border-[#E8EAE9] p-3">
            <div className="flex justify-between text-sm">
              <div>
                <div className="font-medium">{l.batch_number || "—"}</div>
                <div className="text-[11px] text-ink-soft">
                  Received {new Date(l.received_at).toLocaleDateString("en-IN")}
                  {l.invoice_number ? <> · Invoice {l.invoice_number}</> : null}
                </div>
              </div>
              <div className="text-right">
                <div className="font-display text-lg">{remaining} / {received} <span className="text-xs text-ink-soft">{unit || ""}</span></div>
                <div className="text-[11px] text-ink-mid">@ ₹{Number(l.unit_cost || 0).toLocaleString("en-IN")}</div>
              </div>
            </div>
            {received > 0 && (
              <div className="mt-2 h-1 bg-linen rounded-full overflow-hidden">
                <div className="h-full bg-pine transition-all" style={{ width: `${consumedPct}%` }} />
              </div>
            )}
            <div className="mt-1 flex justify-between text-[10px] uppercase tracking-widest text-ink-soft">
              <span>{l.expiry_date ? `Exp ${l.expiry_date}` : "No expiry"}</span>
              <span>{consumedPct}% used</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const V2_LABEL = {
  receive: "Received", treatment: "Consumed (kit)",
  manual_consume: "Consumed", adjustment: "Adjusted",
  expired: "Expired", damaged: "Damaged", returned: "Returned",
  transfer: "Transferred",
};

function MovementList({ rows, unit, emptyText, testid }) {
  if (!rows?.length) return <EmptyState text={emptyText || "No movements recorded yet."} />;
  return (
    <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid={testid}>
      {rows.map((m) => {
        // Dual-schema renderer — same as before, plus balance_before + ref.patient/treatment.
        const isV2 = !!m.movement_type;
        const qty  = isV2 ? Number(m.qty || 0) : Number(m.delta || 0);
        const kind = qty > 0 ? "in" : qty < 0 ? "out" : "adj";
        const verb = isV2 ? (V2_LABEL[m.movement_type] || "Moved")
                          : (m.kind === "in" ? "Added" : m.kind === "out" ? "Removed" : "Adjusted");
        const reason = m.reason || m.notes || "";
        const byName = m.by_name || m.recorded_by_name || (m.ref?.recorded_by_name) || (m.by ? "user" : "—");
        const byRole = m.by_role || "";
        const patient   = m.ref?.patient_name || "";
        const treatment = m.ref?.treatment    || "";
        const bBefore = m.balance_before;
        const bAfter  = m.balance_after;
        const badgeCls =
          kind === "in"  ? "bg-[#E8F0EB] text-[#2D6A4F]" :
          kind === "out" ? "bg-[#FDF3ED] text-[#D06A42]" :
                           "bg-linen text-ink";
        const Icon = kind === "in" ? ArrowDownCircle : kind === "out" ? ArrowUpCircle : Edit3;
        return (
          <div key={m.id} className="flex items-start gap-3 p-3 rounded-xl border border-[#E8EAE9]">
            <div className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${badgeCls}`}>
              <Icon className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex justify-between text-sm">
                <span className="font-medium">
                  {verb} {Math.abs(qty)} {unit || ""}
                </span>
                <span className="text-xs text-ink-soft">{new Date(m.at).toLocaleString("en-IN")}</span>
              </div>
              {reason && <div className="text-xs text-ink-mid mt-1">{reason}</div>}
              {(patient || treatment) && (
                <div className="text-[11px] text-pine mt-0.5">
                  {patient && <>Patient: <strong>{patient}</strong></>}
                  {patient && treatment && " · "}
                  {treatment && <>Treatment: <strong>{treatment}</strong></>}
                </div>
              )}
              {bBefore != null && bAfter != null && (
                <div className="text-[11px] text-ink-mid mt-0.5">
                  Before <strong>{Number(bBefore).toFixed(2)}</strong>
                  {" → "}
                  After <strong>{Number(bAfter).toFixed(2)}</strong>
                  {m.unit_cost ? <> · @ ₹{Number(m.unit_cost).toFixed(2)}</> : null}
                </div>
              )}
              <div className="text-[10px] text-ink-soft mt-1 uppercase tracking-widest">
                by {byName}{byRole ? ` (${byRole})` : ""}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EmptyState({ text }) {
  return <div className="py-8 text-center text-ink-soft text-sm">{text}</div>;
}

// -----------------------------------------------------------------------------
function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8EAE9] sticky top-0 bg-white z-10">
          <h2 className="font-display text-xl">{title}</h2>
          <button onClick={onClose} className="text-ink-soft hover:text-ink"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-ink/80 mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function StyleShim() {
  return <style>{`.input{width:100%;border-radius:12px;border:1px solid #E8EAE9;background:#fff;padding:10px 14px;font-size:14px;outline:none;transition:box-shadow .15s,border-color .15s}.input:focus{border-color:#1A362B;box-shadow:0 0 0 3px rgba(26,54,43,.12)}`}</style>;
}

// -----------------------------------------------------------------------------
// V2 drawers — each posts to the FIFO engine and refreshes the list.
// -----------------------------------------------------------------------------

/** Compact product picker shared by all three drawers. */
function ProductPicker({ items, value, onChange, testid }) {
  const [q, setQ] = useState("");
  const list = useMemo(
    () => (items || []).filter(it => (it.name || "").toLowerCase().includes(q.toLowerCase())).slice(0, 20),
    [items, q]
  );
  const selected = items?.find(i => i.id === value);
  return (
    <div>
      <div className="relative">
        <Search className="w-4 h-4 text-ink-soft absolute left-3 top-3.5" />
        <input
          className="input pl-9"
          placeholder={selected ? selected.name : "Search product…"}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid={`${testid}-search`}
        />
      </div>
      {q && (
        <div className="mt-1 border border-[#E8EAE9] rounded-xl max-h-56 overflow-y-auto bg-white">
          {list.length === 0 ? (
            <div className="px-3 py-2 text-xs text-ink-soft">No matches</div>
          ) : list.map((it) => (
            <button key={it.id} type="button" onClick={() => { onChange(it.id); setQ(""); }}
                    data-testid={`${testid}-opt-${it.id}`}
                    className="w-full text-left px-3 py-2 hover:bg-linen text-sm flex items-center justify-between">
              <span>{it.name}</span>
              <span className="text-xs text-ink-soft">
                {Number(it.stock || 0)} {it.unit || ""}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** GRN — receive one invoice with N product lines. Every line becomes its
 *  own lot + movement through the FIFO engine (bulk endpoint). */
function GRNDialog({ items, onClose, onSaved }) {
  // Stable per-line key so React doesn't reuse row inputs when a middle line
  // is removed (which would otherwise carry the deleted row's cursor/focus
  // state into a neighbour). `_k` is stripped before hitting the API.
  const nextKey = () => `grn_${Math.random().toString(36).slice(2, 9)}`;
  const emptyLine = () => ({ _k: nextKey(), item_id: "", qty: 1, unit_cost: 0, batch_number: "", expiry_date: "", remarks: "" });
  const [lines, setLines] = useState([emptyLine()]);
  const [invoice_number, setInvoice] = useState("");
  const [invoice_date, setInvoiceDate] = useState("");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  // Supplier picker + inline add — spec section 3/4.
  const [suppliers, setSuppliers] = useState([]);
  const [supplier_id, setSupplierId] = useState("");
  const [addSupOpen, setAddSupOpen] = useState(false);
  useEffect(() => {
    api.get("/suppliers").then(r => setSuppliers(r.data || [])).catch(() => {});
  }, []);
  const reloadSuppliers = async (newId) => {
    const { data } = await api.get("/suppliers");
    setSuppliers(data || []);
    if (newId) setSupplierId(newId);
  };

  const setLine = (i, patch) => setLines(lines.map((l, ix) => ix === i ? { ...l, ...patch } : l));
  const addLine = () => setLines([...lines, emptyLine()]);
  const rmLine  = (i) => setLines(lines.length > 1 ? lines.filter((_, ix) => ix !== i) : lines);

  const total = lines.reduce((s, l) => s + (Number(l.qty) * Number(l.unit_cost) || 0), 0);
  const canSubmit = lines.every(l => l.item_id && Number(l.qty) > 0);

  const submit = async () => {
    if (!canSubmit) return toast.error("Every row needs a product and qty > 0");
    setSaving(true);
    try {
      const { data } = await api.post("/inventory/grn/bulk", {
        supplier_id:    supplier_id || null,
        invoice_number: invoice_number || null,
        invoice_date:   invoice_date || null,
        remarks,
        lines: lines.map(l => ({
          item_id: l.item_id,
          qty: Number(l.qty),
          unit_cost: Number(l.unit_cost) || 0,
          batch_number: l.batch_number || null,
          expiry_date: l.expiry_date || null,
          remarks: l.remarks || "",
        })),
      });
      toast.success(`Received ${data.lines_received} product${data.lines_received === 1 ? "" : "s"}`);
      onSaved();
    } catch (e) {
      toast.error(apiError(e, "GRN failed"));
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Receive stock" onClose={onClose}>
      <div className="space-y-4" data-testid="grn-dialog">
        {/* Invoice-level fields */}
        <Field label="Supplier">
          <div className="flex gap-2 items-start">
            <select className="input flex-1" value={supplier_id}
                    onChange={(e) => setSupplierId(e.target.value)}
                    data-testid="grn-supplier">
              <option value="">— Select supplier (optional) —</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <button type="button" onClick={() => setAddSupOpen(true)}
                    data-testid="grn-add-supplier"
                    className="shrink-0 rounded-full border border-[#E8EAE9] bg-white text-ink text-xs px-3 py-2 hover:border-pine">
              + Add
            </button>
          </div>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Invoice number">
            <input className="input" value={invoice_number}
                   onChange={(e) => setInvoice(e.target.value)} data-testid="grn-invoice" />
          </Field>
          <Field label="Invoice date">
            <DateDMYInput className="input" value={invoice_date}
                   onChange={(e) => setInvoiceDate(e.target.value)} data-testid="grn-invoice-date" />
          </Field>
        </div>

        {/* Product lines */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <div className="text-xs font-medium text-ink/80">Products in this invoice</div>
            <button onClick={addLine} data-testid="grn-add-line"
                    className="text-xs text-pine inline-flex items-center gap-1">
              <Plus className="w-3 h-3" /> Add another product
            </button>
          </div>
          <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
            {lines.map((l, i) => (
              <div key={l._k || i} className="rounded-2xl border border-[#E8EAE9] p-3 bg-linen/40" data-testid={`grn-line-${i}`}>
                <div className="flex justify-between items-center mb-2">
                  <div className="text-[11px] uppercase tracking-widest text-ink-soft">Product {i + 1}</div>
                  {lines.length > 1 && (
                    <button onClick={() => rmLine(i)} data-testid={`grn-line-rm-${i}`}
                            className="text-ink-mid hover:text-[#B94A48]"><X className="w-4 h-4" /></button>
                  )}
                </div>
                <ProductPicker items={items} value={l.item_id}
                               onChange={(id) => setLine(i, { item_id: id })}
                               testid={`grn-line-product-${i}`} />
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <input type="number" min="0" step="any" className="input" placeholder="Quantity"
                         value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })}
                         data-testid={`grn-line-qty-${i}`} />
                  <input type="number" min="0" step="any" className="input" placeholder="Unit cost ₹"
                         value={l.unit_cost} onChange={(e) => setLine(i, { unit_cost: e.target.value })}
                         data-testid={`grn-line-cost-${i}`} />
                  <input className="input" placeholder="Batch"
                         value={l.batch_number} onChange={(e) => setLine(i, { batch_number: e.target.value })}
                         data-testid={`grn-line-batch-${i}`} />
                  <DateDMYInput className="input"
                         value={l.expiry_date} onChange={(e) => setLine(i, { expiry_date: e.target.value })}
                         data-testid={`grn-line-expiry-${i}`} />
                </div>
                <div className="mt-1 text-right text-[11px] text-ink-mid">
                  Line total: <strong>₹{(Number(l.qty) * Number(l.unit_cost) || 0).toLocaleString("en-IN")}</strong>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Field label="Invoice remarks (optional)">
          <input className="input" value={remarks} onChange={(e) => setRemarks(e.target.value)}
                 data-testid="grn-remarks" />
        </Field>

        <div className="flex justify-between items-center pt-3 border-t border-[#F0EEE8]">
          <div className="text-sm">
            Invoice total: <strong>₹{total.toLocaleString("en-IN")}</strong>
            <span className="text-ink-soft"> · {lines.length} product{lines.length === 1 ? "" : "s"}</span>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-full text-sm text-ink-mid hover:bg-linen">Cancel</button>
            <button onClick={submit} disabled={saving || !canSubmit} data-testid="grn-submit"
                    className="px-5 py-2 rounded-full bg-pine text-linen text-sm font-medium hover:bg-pine/90 disabled:opacity-50">
              {saving ? "Saving…" : `Receive ${lines.length} product${lines.length === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      </div>
      {addSupOpen && (
        <QuickAddSupplierDialog
          onClose={() => setAddSupOpen(false)}
          onSaved={(sup) => { setAddSupOpen(false); reloadSuppliers(sup.id); toast.success("Supplier added"); }}
        />
      )}
      <StyleShim />
    </Modal>
  );
}

/** Tiny popup: create a supplier without leaving the GRN drawer. */
function QuickAddSupplierDialog({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", phone: "", email: "", gst: "", address: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const { data } = await api.post("/suppliers", f);
      onSaved(data);
    } catch (e) { toast.error(apiError(e, "Failed to save supplier")); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Add supplier" onClose={onClose}>
      <div className="space-y-3" data-testid="quick-supplier-dialog">
        <Field label="Name"><input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="qsup-name" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone"><input className="input" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="qsup-phone" /></Field>
          <Field label="Email"><input className="input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="qsup-email" /></Field>
          <Field label="GST (optional)"><input className="input" value={f.gst} onChange={(e) => setF({ ...f, gst: e.target.value })} data-testid="qsup-gst" /></Field>
          <Field label="Address (optional)"><input className="input" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="qsup-address" /></Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm text-ink-mid hover:bg-linen">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="qsup-save"
                  className="px-5 py-2 rounded-full bg-pine text-linen text-sm font-medium hover:bg-pine/90 disabled:opacity-50">
            {saving ? "Saving…" : "Save supplier"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** Manual Consumption — FIFO deduct, tied to a reason / optional treatment.
 *  Redesign (spec §Manual Consumption):
 *    - Show current stock the moment a product is picked
 *    - Predefined reason dropdown + freetext when Other
 *    - Optional Patient picker (search) + Treatment freetext
 *    - Live "Remaining after deduction" preview
 *    - Client guard: prevent qty > available
 *    - Kit warning: if product is used in any active treatment kit, show
 *      a confirm ("already auto-deducted, continue anyway?"). */
const CONSUME_REASONS = [
  { code: "clinical_procedure", label: "Clinical Procedure" },
  { code: "emergency_usage",    label: "Emergency Usage" },
  { code: "sample",             label: "Sample" },
  { code: "damaged",            label: "Damaged" },
  { code: "expired",            label: "Expired" },
  { code: "lost",               label: "Lost" },
  { code: "staff_training",     label: "Staff Training" },
  { code: "other",              label: "Other" },
];

function ConsumeDialog({ items, onClose, onSaved }) {
  const [item_id, setItemId] = useState("");
  const [qty, setQty] = useState("");
  const [reason_code, setReasonCode] = useState("clinical_procedure");
  const [reason_other, setReasonOther] = useState("");
  const [treatment, setTreatment] = useState("");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  // Patient picker — async search against /api/patients (returns full list).
  const [patients, setPatients] = useState([]);
  const [patient_id, setPatientId] = useState("");
  const [patient_name, setPatientName] = useState("");
  const [pq, setPq] = useState("");
  // Kit warning — becomes non-null when the picked product is in an
  // active treatment kit; user must confirm before submit.
  const [kitWarning, setKitWarning] = useState(null);   // { kits: [{kit_name, auto_qty}], acknowledged }

  useEffect(() => {
    api.get("/patients").then(r => setPatients(r.data || [])).catch(() => {});
  }, []);

  // When the product changes, refresh the kit-usage lookup so we can warn
  // BEFORE the user fills the rest of the form (spec: prevent mistakes).
  useEffect(() => {
    if (!item_id) { setKitWarning(null); return; }
    api.get(`/inventory/kits-using-item/${item_id}`)
       .then(r => {
         const kits = r.data?.kits || [];
         setKitWarning(kits.length > 0 ? { kits, acknowledged: false } : null);
       }).catch(() => setKitWarning(null));
  }, [item_id]);

  const selected      = items?.find(i => i.id === item_id);
  const currentStock  = Number(selected?.stock || 0);
  const qtyNum        = Number(qty) || 0;
  const overspend     = qtyNum > currentStock;
  const remainingPreview = Math.max(0, currentStock - qtyNum);

  // Patient combobox (client-side filter — /patients returns up to 500 rows).
  const patientMatches = useMemo(() => {
    const query = pq.trim().toLowerCase();
    if (!query) return [];
    return (patients || [])
      .filter(p =>
        (p.name || "").toLowerCase().includes(query) ||
        (p.phone || "").toString().includes(query))
      .slice(0, 8);
  }, [patients, pq]);

  const canSubmit = item_id && qtyNum > 0 && !overspend &&
                    (reason_code !== "other" || reason_other.trim());

  const submit = async () => {
    if (!canSubmit) return;
    if (kitWarning && !kitWarning.acknowledged) {
      const msg = `This product is already deducted automatically through ${kitWarning.kits.length} Treatment Kit${kitWarning.kits.length > 1 ? "s" : ""} (${kitWarning.kits.map(k => k.kit_name).join(", ")}).\n\nManual deduction may duplicate stock. Continue?`;
      if (!window.confirm(msg)) return;
      setKitWarning({ ...kitWarning, acknowledged: true });
    }
    setSaving(true);
    try {
      const { data } = await api.post("/inventory/consume", {
        item_id, qty: qtyNum,
        reason_code,
        reason_other: reason_code === "other" ? reason_other.trim() : null,
        treatment: treatment || null,
        patient_id: patient_id || null,
        patient_name: patient_name || null,
        remarks,
      });
      toast.success(`Consumed ${qtyNum} from ${data.item_name}. On hand: ${data.on_hand}`);
      onSaved();
    } catch (e) {
      toast.error(apiError(e, "Consumption failed"));
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Manual consumption" onClose={onClose}>
      <div className="space-y-4" data-testid="consume-dialog">
        {/* Product picker + live current-stock display */}
        <Field label="Product">
          <ProductPicker items={items} value={item_id} onChange={setItemId} testid="consume-product" />
        </Field>
        {selected && (
          <div className="rounded-2xl border border-[#E8EAE9] bg-linen/40 px-4 py-3 flex justify-between items-center" data-testid="consume-current-stock">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-ink-soft">Current stock</div>
              <div className="font-display text-2xl leading-none">{currentStock} <span className="text-xs text-ink-soft">{selected.unit || ""}</span></div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-widest text-ink-soft">Avg cost</div>
              <div className="font-display text-lg">₹{Number(selected.unit_cost || 0).toLocaleString("en-IN")}</div>
            </div>
          </div>
        )}
        {/* Kit warning banner — shows BEFORE the user submits. */}
        {kitWarning && (
          <div className="rounded-2xl border border-[#EDC7C6] bg-[#FCECEC] px-4 py-3 text-sm" data-testid="consume-kit-warning">
            <div className="font-medium text-[#B94A48]">⚠︎ Auto-deducted via Treatment Kit</div>
            <div className="text-xs text-ink-mid mt-1">
              This product is already deducted through:&nbsp;
              <strong>{kitWarning.kits.map(k => k.kit_name).join(", ")}</strong>.
              Manual deduction may duplicate stock.
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label={`Quantity${selected ? ` (${selected.unit || "units"})` : ""}`}>
            <input type="number" min="0" step="any" className={`input ${overspend ? "!border-[#B94A48]" : ""}`}
                   value={qty} onChange={(e) => setQty(e.target.value)}
                   data-testid="consume-qty" placeholder="0" autoFocus />
            {overspend && (
              <div className="text-[11px] text-[#B94A48] mt-1" data-testid="consume-overspend">
                Cannot exceed available stock ({currentStock})
              </div>
            )}
          </Field>
          <Field label="Reason">
            <select className="input" value={reason_code}
                    onChange={(e) => setReasonCode(e.target.value)}
                    data-testid="consume-reason-code">
              {CONSUME_REASONS.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
            </select>
          </Field>
        </div>
        {reason_code === "other" && (
          <Field label="Specify reason">
            <input className="input" value={reason_other}
                   onChange={(e) => setReasonOther(e.target.value)}
                   data-testid="consume-reason-other"
                   placeholder="e.g. lent to affiliated clinic" />
          </Field>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Patient (optional)">
            <div className="relative">
              <input className="input" value={pq || patient_name}
                     onChange={(e) => { setPq(e.target.value); setPatientId(""); setPatientName(""); }}
                     data-testid="consume-patient-search"
                     placeholder="Search patient by name or phone" />
              {pq && patientMatches.length > 0 && (
                <div className="absolute z-20 left-0 right-0 mt-1 border border-[#E8EAE9] rounded-xl max-h-56 overflow-y-auto bg-white shadow-lg">
                  {patientMatches.map((p) => (
                    <button key={p.id} type="button"
                            onClick={() => { setPatientId(p.id); setPatientName(p.name || ""); setPq(""); }}
                            data-testid={`consume-patient-opt-${p.id}`}
                            className="w-full text-left px-3 py-2 hover:bg-linen text-sm">
                      <div className="font-medium">{p.name}</div>
                      <div className="text-[11px] text-ink-soft">{p.phone || p.email || ""}</div>
                    </button>
                  ))}
                </div>
              )}
              {patient_id && (
                <div className="mt-1 inline-flex items-center gap-2 text-[11px] text-ink-mid">
                  Linked to <strong>{patient_name}</strong>
                  <button onClick={() => { setPatientId(""); setPatientName(""); }}
                          data-testid="consume-patient-clear"
                          className="text-ink-soft hover:text-[#B94A48]">Clear</button>
                </div>
              )}
            </div>
          </Field>
          <Field label="Treatment (optional)">
            <input className="input" value={treatment}
                   onChange={(e) => setTreatment(e.target.value)}
                   data-testid="consume-treatment"
                   placeholder="e.g. RCT, Filling" />
          </Field>
        </div>
        <Field label="Remarks (optional)">
          <input className="input" value={remarks} onChange={(e) => setRemarks(e.target.value)}
                 data-testid="consume-remarks" />
        </Field>

        {/* Preview strip — reads current stock, shows remaining after deduct */}
        {selected && qtyNum > 0 && !overspend && (
          <div className="rounded-2xl border border-[#E8EAE9] bg-white px-4 py-3 flex items-center justify-between text-sm" data-testid="consume-preview">
            <span className="text-ink-mid">After deduction, remaining stock will be</span>
            <span className="font-display text-2xl text-pine">{remainingPreview} <span className="text-xs text-ink-soft">{selected.unit || ""}</span></span>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm text-ink-mid hover:bg-linen">Cancel</button>
          <button onClick={submit} disabled={saving || !canSubmit} data-testid="consume-submit"
                  className="px-5 py-2 rounded-full bg-pine text-linen text-sm font-medium hover:bg-pine/90 disabled:opacity-50">
            {saving ? "Deducting…" : "Deduct stock"}
          </button>
        </div>
      </div>
      <StyleShim />
    </Modal>
  );
}

/** Adjustment — +/- for audit / expired / damaged / returned / transfer.
 *  Uses the same predefined-reason dropdown as Manual Consumption so the
 *  audit ledger is stripped of ambiguous free-text. */
const ADJUST_TYPES = [
  { value: "adjustment", label: "Audit / count correction" },
  { value: "expired",    label: "Expired" },
  { value: "damaged",    label: "Damaged" },
  { value: "returned",   label: "Returned to supplier" },
  { value: "transfer",   label: "Transfer" },
];

function AdjustDialog({ items, onClose, onSaved }) {
  const [item_id, setItemId] = useState("");
  const [delta, setDelta] = useState("");
  const [movement_type, setType] = useState("adjustment");
  const [reason_code, setReasonCode] = useState("clinical_procedure");
  const [reason_other, setReasonOther] = useState("");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  const selected     = items?.find(i => i.id === item_id);
  const currentStock = Number(selected?.stock || 0);
  const deltaNum     = Number(delta) || 0;
  const nextStock    = currentStock + deltaNum;
  const overspend    = deltaNum < 0 && Math.abs(deltaNum) > currentStock;

  const canSubmit = item_id && deltaNum !== 0 && !overspend &&
                    (reason_code !== "other" || reason_other.trim());

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const { data } = await api.post("/inventory/adjust", {
        item_id, delta: deltaNum, movement_type,
        reason_code,
        reason_other: reason_code === "other" ? reason_other.trim() : null,
        remarks,
      });
      toast.success(`Adjusted ${data.item_name}. On hand: ${data.on_hand}`);
      onSaved();
    } catch (e) {
      toast.error(apiError(e, "Adjustment failed"));
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Stock adjustment" onClose={onClose}>
      <div className="space-y-4" data-testid="adjust-dialog">
        <Field label="Product">
          <ProductPicker items={items} value={item_id} onChange={setItemId} testid="adjust-product" />
        </Field>
        {selected && (
          <div className="rounded-2xl border border-[#E8EAE9] bg-linen/40 px-4 py-3 flex justify-between items-center">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-ink-soft">Current stock</div>
              <div className="font-display text-2xl leading-none">{currentStock} <span className="text-xs text-ink-soft">{selected.unit || ""}</span></div>
            </div>
            {deltaNum !== 0 && !overspend && (
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-widest text-ink-soft">After adjustment</div>
                <div className={`font-display text-2xl ${deltaNum > 0 ? "text-[#2D6A4F]" : "text-[#D06A42]"}`}>
                  {nextStock} <span className="text-xs text-ink-soft">{selected.unit || ""}</span>
                </div>
              </div>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Delta (+/-)">
            <input type="number" step="any" className={`input ${overspend ? "!border-[#B94A48]" : ""}`}
                   value={delta} onChange={(e) => setDelta(e.target.value)}
                   data-testid="adjust-delta" placeholder="0" />
            {overspend && <div className="text-[11px] text-[#B94A48] mt-1">Cannot subtract more than {currentStock}</div>}
          </Field>
          <Field label="Type">
            <select className="input" value={movement_type} onChange={(e) => setType(e.target.value)}
                    data-testid="adjust-type">
              {ADJUST_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Reason">
            <select className="input" value={reason_code}
                    onChange={(e) => setReasonCode(e.target.value)}
                    data-testid="adjust-reason-code">
              {CONSUME_REASONS.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
            </select>
          </Field>
          <Field label="Remarks">
            <input className="input" value={remarks} onChange={(e) => setRemarks(e.target.value)}
                   data-testid="adjust-remarks" />
          </Field>
        </div>
        {reason_code === "other" && (
          <Field label="Specify reason">
            <input className="input" value={reason_other}
                   onChange={(e) => setReasonOther(e.target.value)}
                   data-testid="adjust-reason-other"
                   placeholder="Describe the adjustment" />
          </Field>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm text-ink-mid hover:bg-linen">Cancel</button>
          <button onClick={submit} disabled={saving || !canSubmit} data-testid="adjust-submit"
                  className="px-5 py-2 rounded-full bg-pine text-linen text-sm font-medium hover:bg-pine/90 disabled:opacity-50">
            {saving ? "Saving…" : "Save adjustment"}
          </button>
        </div>
      </div>
      <StyleShim />
    </Modal>
  );
}
